import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Typography,
  Divider,
  Switch,
} from '@mui/material';
import {
  LogOut,
  User,
  Settings,
  LayoutDashboard,
  Globe,
  Focus,
  Zap,
  AlignJustify,
  Building2,
  Check,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { colors, radius } from '@/theme/tokens';
import { useBusinessStore, usePreferencesStore } from '@/stores';
import { authApi } from '@/api';

export function UserMenu() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout, isAuthenticated } = useAuth0();
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const { focusMode, setFocusMode, reduceMotion, setReduceMotion, density, setDensity } =
    usePreferencesStore();
  const memberships = useBusinessStore((s) => s.memberships);
  const activeBusinessId = useBusinessStore((s) => s.activeBusinessId);
  const setActiveBusinessId = useBusinessStore((s) => s.setActiveBusinessId);

  if (!isAuthenticated || !user) {
    return null;
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDashboardClick = () => {
    handleClose();
    navigate('/admin/dashboard');
  };

  const handleProfileClick = () => {
    handleClose();
    navigate('/admin/profile');
  };

  const handleSettingsClick = () => {
    handleClose();
    navigate('/admin/settings');
  };

  const handleLogout = () => {
    handleClose();
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  };

  const handleLanguageToggle = async () => {
    handleClose();
    const nextLocale = i18n.language === 'es-MX' ? 'en-US' : 'es-MX';
    try {
      await authApi.updateMe({ locale: nextLocale });
      await i18n.changeLanguage(nextLocale === 'en-US' ? 'en' : nextLocale);
    } catch (error) {
      console.error('[UserMenu] Failed to save language preference:', error);
    }
  };

  const handleBusinessSwitch = async (businessId: string) => {
    if (businessId === activeBusinessId) {
      handleClose();
      return;
    }

    setActiveBusinessId(businessId);
    await queryClient.cancelQueries();
    queryClient.clear();
    handleClose();
    navigate('/admin/dashboard');
  };

  const initials =
    user.name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) ||
    user.email?.[0]?.toUpperCase() ||
    '?';

  return (
    <>
      <IconButton
        onClick={handleClick}
        size="small"
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Avatar
          src={user.picture}
          alt={user.name || user.email}
          sx={{
            width: 36,
            height: 36,
            bgcolor: colors.accent,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {initials}
        </Avatar>
      </IconButton>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              mt: 1,
              minWidth: 200,
              borderRadius: radius.lg,
              '& .MuiMenuItem-root': {
                px: 2,
                py: 1.5,
                gap: 1.5,
                fontSize: 14,
              },
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user.name || 'User'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleDashboardClick}>
          <LayoutDashboard size={18} />
          {t('userMenu.dashboard')}
        </MenuItem>
        <MenuItem onClick={handleProfileClick}>
          <User size={18} />
          {t('userMenu.profile')}
        </MenuItem>
        <MenuItem onClick={handleSettingsClick}>
          <Settings size={18} />
          {t('userMenu.settings')}
        </MenuItem>
        <MenuItem onClick={handleLanguageToggle}>
          <Globe size={18} />
          {i18n.language === 'es-MX' ? t('language.en') : t('language.esMX')}
        </MenuItem>
        {memberships.length > 0 && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {memberships.length > 1 ? 'Switch business' : 'Current business'}
              </Typography>
            </Box>
            {memberships.map((membership) => {
              const isActive = membership.businessId === activeBusinessId;
              const isDisabled = membership.status !== 'active' && membership.status !== 'approved';

              return (
                <MenuItem
                  key={membership.id}
                  disabled={isDisabled}
                  onClick={() => {
                    void handleBusinessSwitch(membership.businessId);
                  }}
                >
                  <Building2 size={18} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                      {membership.businessName || membership.businessId}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {membership.role}{' '}
                      {membership.status !== 'active' ? `• ${membership.status}` : ''}
                    </Typography>
                  </Box>
                  {isActive && <Check size={16} color={colors.accent} />}
                </MenuItem>
              );
            })}
          </>
        )}
        <Divider />
        {/* ADHD Accommodation Toggles */}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setFocusMode(!focusMode);
          }}
          sx={{ justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Focus size={18} />
            <span>{t('userMenu.focusMode')}</span>
          </Box>
          <Switch
            size="small"
            checked={focusMode}
            onChange={() => setFocusMode(!focusMode)}
            onClick={(e) => e.stopPropagation()}
          />
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setReduceMotion(!reduceMotion);
          }}
          sx={{ justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Zap size={18} />
            <span>{t('userMenu.reduceMotion')}</span>
          </Box>
          <Switch
            size="small"
            checked={reduceMotion}
            onChange={() => setReduceMotion(!reduceMotion)}
            onClick={(e) => e.stopPropagation()}
          />
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setDensity(
              density === 'compact'
                ? 'comfortable'
                : density === 'comfortable'
                  ? 'spacious'
                  : 'compact'
            );
          }}
        >
          <AlignJustify size={18} />
          <span>
            {t('userMenu.density')}:{' '}
            {density === 'compact'
              ? t('userMenu.densityCompact')
              : density === 'comfortable'
                ? t('userMenu.densityComfortable')
                : t('userMenu.densitySpacious')}
          </span>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <LogOut size={18} />
          {t('userMenu.signOut')}
        </MenuItem>
      </Menu>
    </>
  );
}
